import { baseApi } from './baseApi';

const BASE_URL = '/api/v1/admin/market-report';

export type ExportStateOfMarketParams = {
  filename: string;
};

export type MarketSnapshotResponse = {
  snapshotId: string;
  /** ISO-8601 instant with microsecond precision. */
  snapshotAt: string;
  /** Positions in the snapshot, bids and offers together. */
  rowCount: number;
};

export const marketReportApi = baseApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Downloads the State of the Market CSV.
     *
     * The request goes through RTK Query rather than a plain link or `window.open` because the
     * API is authorized with a bearer token held in memory — a bare navigation would arrive
     * unauthenticated. The blob is turned into a download client-side, the same approach as
     * `exportOfferMetrics`.
     *
     * The caller supplies the filename rather than this reading it from the response's
     * Content-Disposition. The API is on a different origin than the app in every environment,
     * and Content-Disposition is not a CORS-safelisted response header, so
     * `headers.get('Content-Disposition')` returns null in the browser however faithfully the
     * backend sets it. The backend still sends the header for direct API consumers.
     */
    exportStateOfMarket: build.query<null, ExportStateOfMarketParams>({
      query: ({ filename }) => ({
        url: `${BASE_URL}/export`,
        responseHandler: async (response: Response) => {
          if (!response.ok) return response.json();

          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          return null;
        },
      }),
      // The report is a point-in-time snapshot; a cached response would hand back stale data
      // on the next click.
      keepUnusedDataFor: 0,
    }),
    /**
     * Publishes a State of the Market snapshot to Snowflake for the commercial team's Sigma
     * reporting (RME-1383). The backend answers 409 while another snapshot is running and 503
     * where Snowflake streaming is turned off, each with a message fit to show the admin.
     */
    publishMarketSnapshot: build.mutation<MarketSnapshotResponse, void>({
      query: () => ({
        url: `${BASE_URL}/snapshot`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useLazyExportStateOfMarketQuery,
  usePublishMarketSnapshotMutation,
} = marketReportApi;
